import ApplicationContexts from '@app/src/ApplicationContexts'

import { CssBaseline, StyledEngineProvider, ThemeProvider } from '@mui/material'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createBrowserHistory } from 'history'
import { withLDProvider } from 'launchdarkly-react-client-sdk'
import React, { lazy, Suspense, useMemo } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { IntlProvider } from 'react-intl'
import { Route, Router, Switch } from 'react-router-dom'
import App from './App'
import { SimplifiedRoutes } from './appRoutes'
import Authentication from './Authentication'
import GlobalError, { logError } from './components/GlobalError'
import WfLoader from './components/WfLoader'
import { Config } from './config'
import { useLocale } from './context/LocaleContext'
import LocaleContextProvider from './context/LocaleContextProvider'
import useTranslationMessages from './hooks/useTranslationMessages'
import { getLocale } from './i18n'
import sentryService from './services/SentryService'
import getTheme from './theme'

const ReferralLandingPage = lazy(() => import('./pages/ReferralLandingPage/ReferralLandingPage'))

const history = createBrowserHistory()

// Must live outside the component: a new QueryClient discards the entire cache,
// and this component re-renders when the locale resolves and when LaunchDarkly
// identifies the user.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

sentryService.init(history, SimplifiedRoutes)

const AppContainer: React.FC = () => {
  const { locale } = useLocale()
  const language = getLocale(locale)
  const messages = useTranslationMessages(language)

  const theme = useMemo(() => getTheme(locale), [locale])

  return (
    <IntlProvider locale={language} messages={messages}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <ErrorBoundary FallbackComponent={GlobalError} onError={(error, info) => logError(error, info)}>
            <QueryClientProvider client={queryClient}>
              <Switch>
                <Route path="/referral/:code">
                  <Suspense fallback={<WfLoader />}>
                    <ReferralLandingPage />
                  </Suspense>
                </Route>
                <Route>
                  <ApplicationContexts>
                    <App />
                  </ApplicationContexts>
                </Route>
              </Switch>
            </QueryClientProvider>
          </ErrorBoundary>
        </ThemeProvider>
      </StyledEngineProvider>
    </IntlProvider>
  )
}

const LDProvider = withLDProvider({
  clientSideID: Config.LAUNCH_DARKLY_CLIENT_SIDE_ID ?? '',
  options: {
    /* ... */
  },
})(AppContainer)

const Provider: React.FC = () => {
  return (
    <LocaleContextProvider>
      <Router history={history}>
        <Authentication>
          <LDProvider />
        </Authentication>
      </Router>
    </LocaleContextProvider>
  )
}

export default Provider
